import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiArrowRight, FiHeart } from "react-icons/fi";
import productData from "../Datas/productData";
import "../css/productdetails.scss";
import { CartContext } from "./CartContext";
import { formatCurrency, siteConfig } from "../config/siteConfig";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, toggleWishlist, isWishlisted } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [adding, setAdding] = useState(false);
  const [loadedImages, setLoadedImages] = useState([]);
  const [failedImages, setFailedImages] = useState([]);

  useEffect(() => {
    setSelectedSize(product?.size?.[0] || "");
    setSelectedColor(product?.colors?.[0] || "");
    setAdding(false);
    setLoadedImages([]);
    setFailedImages([]);
    if (product) document.title = `${product.name} — ${siteConfig.brandName}`;
  }, [product]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return productData
      .filter(
        (item) =>
          item.id !== product.id &&
          (item.category === product.category || item.maker === product.maker)
      )
      .slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main id="main-content" className="product-not-found">
        <p className="eyebrow">404 / Object not found</p>
        <h1>This object is no longer in the collection.</h1>
        <Link className="button-outline" to="/shop">Return to shop</Link>
      </main>
    );
  }

  const handleAddToCart = async () => {
    if (adding || product.stock < 1) return;
    setAdding(true);
    await new Promise((resolve) => window.setTimeout(resolve, 320));
    addToCart(product.id, 1, selectedSize, selectedColor);
    navigate("/cartpage");
    window.scrollTo({ top: 0 });
  };

  return (
    <main id="main-content" className="product-page">
      <div className="product-page__layout">
        <section className="product-gallery" aria-label={`${product.name} image gallery`}>
          {product.images.map((image, index) => {
            const imageKey = `${product.id}-${index}`;
            const loaded = loadedImages.includes(imageKey);
            const failed = failedImages.includes(imageKey);

            return (
              <figure className="product-gallery__item" key={imageKey}>
                <div
                  className={`product-gallery__image${loaded ? " is-loaded" : ""}${
                    failed ? " image-fallback" : ""
                  }`}
                >
                  {!failed && (
                    <img
                      src={image}
                      alt={`${product.name} by ${product.maker}, view ${index + 1}`}
                      width="1120"
                      height="1400"
                      decoding="async"
                      loading={index === 0 ? "eager" : "lazy"}
                      onLoad={() => setLoadedImages((items) => [...items, imageKey])}
                      onError={() => setFailedImages((items) => [...items, imageKey])}
                    />
                  )}
                </div>
                {product.images.length > 1 && (
                  <figcaption>{String(index + 1).padStart(2, "0")}</figcaption>
                )}
              </figure>
            );
          })}
        </section>

        <aside className="product-info">
          <header className="product-info__heading">
            <h1>{product.name}</h1>
            <p>{product.maker}</p>
            <h2>{formatCurrency(product.price)}</h2>
          </header>

          {product.colors.length > 0 && (
            <fieldset className="product-options">
              <legend>Colour / finish</legend>
              <div>
                {product.colors.map((color) => (
                  <button
                    type="button"
                    key={color}
                    className={selectedColor === color ? "is-active" : ""}
                    aria-pressed={selectedColor === color}
                    onClick={() => setSelectedColor(color)}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          {product.size.length > 0 && (
            <fieldset className="product-options">
              <legend>Size / edition</legend>
              <div>
                {product.size.map((size) => (
                  <button
                    type="button"
                    key={size}
                    className={selectedSize === size ? "is-active" : ""}
                    aria-pressed={selectedSize === size}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          <div className="product-info__actions">
            <button
              className="product-info__add"
              type="button"
              onClick={handleAddToCart}
              disabled={adding || product.stock < 1}
            >
              {product.stock < 1 ? "Sold out" : adding ? "Adding…" : "Add to basket"}
            </button>
            <button
              className={`product-info__save${isWishlisted(product.id) ? " is-active" : ""}`}
              type="button"
              onClick={() => toggleWishlist(product.id)}
              aria-pressed={isWishlisted(product.id)}
            >
              <FiHeart aria-hidden="true" />
              {isWishlisted(product.id) ? "Saved" : "Save"}
            </button>
          </div>
          <p className="product-info__stock" aria-live="polite">
            {product.stock === 1
              ? "One available"
              : product.stock <= 3
              ? `Small edition — ${product.stock} available`
              : "In stock"}
          </p>

          <details className="product-accordion" open>
            <summary>Description</summary>
            <div>
              {product.description.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </details>

          <details className="product-accordion">
            <summary>Materials & making</summary>
            <dl>
              {Object.entries(product.attributes).map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            <ul>
              {product.features.map((feature) => <li key={feature}>{feature}</li>)}
            </ul>
          </details>

          <details className="product-accordion">
            <summary>Delivery & returns</summary>
            <div>
              <p>{siteConfig.shipping.dispatch}.</p>
              <p>
                Tracked delivery is {formatCurrency(siteConfig.shipping.standard)} per order.
              </p>
              <p>
                <Link className="text-link" to="/shipping">Shipping</Link>
                {" / "}
                <Link className="text-link" to="/returns">Returns</Link>
              </p>
            </div>
          </details>
        </aside>
      </div>

      {relatedProducts.length > 0 && (
        <section className="related-products" aria-labelledby="related-heading">
          <h2 id="related-heading">Related content</h2>
          <div className="related-products__grid">
            {relatedProducts.map((item) => (
              <Link key={item.id} to={`/shopDetails/${item.id}`}>
                <img
                  src={item.image}
                  alt=""
                  width="1120"
                  height="1400"
                  loading="lazy"
                  decoding="async"
                />
                <div>
                  <span>{item.name}</span>
                  <FiArrowRight aria-hidden="true" />
                </div>
                <em>{item.maker}</em>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
